import { useRef, useState } from "react";
import type { LayoutGroup } from "@/hardware/keyboard-layouts/picker";

interface LayoutPickProps {
  groups: LayoutGroup[];
  value: string;
  onChange: (id: string) => void;
  /** Rename the active board layer, the one permitted edit; absent for universal mappings. */
  onRename?: (name: string) => void;
  /** Locked when a board-bound Profile pins the lens. */
  disabled?: boolean;
  /** A leading "follow the other side" option (Compare B-side); "" selects it. Mirrors KeyboardSelect. */
  sameAs?: { label: string } | null;
  /** Defaults to "Keyboard Layout"; the Compare B-side passes "Layout B". */
  ariaLabel?: string;
}

/** The Keyboard-Layout picker: one control, board-dependent contents. */
export function LayoutPick({
  groups,
  value,
  onChange,
  onRename,
  disabled,
  sameAs,
  ariaLabel = "Keyboard Layout",
}: LayoutPickProps) {
  const [editing, setEditing] = useState(false);
  // marks an intentional close (Enter commits, Escape cancels) so the focusout it
  // triggers does not re-commit; a focus-loss blur with no prior key still commits.
  const handled = useRef(false);
  const shown = groups.filter((g) => g.options.length > 0);
  const current = shown.flatMap((g) => g.options).find((o) => o.id === value);

  const commit = (raw: string) => {
    const name = raw.trim();
    if (name && onRename) onRename(name);
    handled.current = true;
    setEditing(false);
  };

  if (editing && onRename) {
    return (
      <input
        className="layout-rename"
        aria-label="Layer name"
        defaultValue={current?.name ?? ""}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit(e.currentTarget.value);
          else if (e.key === "Escape") {
            handled.current = true; // cancel: the unmount focusout must not commit
            setEditing(false);
          }
        }}
        onBlur={(e) => {
          if (!handled.current) commit(e.currentTarget.value);
        }}
      />
    );
  }

  if (shown.length === 0 && !sameAs) {
    return <span className="layout-empty">No selectable layers</span>;
  }

  return (
    <>
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {sameAs ? <option value="">{sameAs.label}</option> : null}
        {shown.length === 1
          ? shown[0]?.options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))
          : shown.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </optgroup>
            ))}
      </select>
      {onRename ? (
        <button
          type="button"
          className="kl-btn ghost"
          onClick={() => {
            handled.current = false; // fresh edit session
            setEditing(true);
          }}
        >
          Rename
        </button>
      ) : null}
    </>
  );
}
